import classNames from 'classnames';
import React from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import ConfirmationModal from '../components/ConfirmationModal';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import { MenuItem, MenuSimpleItem } from '../components/Menu';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { ExternalSearchMode, UserType } from '../graphql/generated/graphql';
import addedIcon from '../icons/non-prefilled/16x16/added.svg';
import clearIcon from '../icons/non-prefilled/16x16/clear.svg';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import { miscToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import Link from '../utils/router/Link';
import * as styles from './SearchPlatformMenuItem.module.scss';

export enum Section {
  Available = 'AVAILABLE',
  Individual = 'INDIVIDUAL',
  Team = 'TEAM',
}

enum Mode {
  Both,
  TeamOnly,
  PersonalOnly,
}

const PLATFORM_FRAGMENT = gql(`
  fragment SearchPlatformMenuItem_platform on ExternalSearchPlatform {
    installModes
    personalAuthorized
    personalAuthUrl
    platform
    teamAuthorized
    teamAuthUrl
  }
`);

interface SearchPlatformMenuItemProps {
  onRemove: () => Promise<unknown>;
  type: Section;
  platform: DocumentType<typeof PLATFORM_FRAGMENT>;
}

const SearchPlatformMenuItem = ({
  onRemove,
  platform: {
    platform,
    installModes,
    personalAuthorized,
    personalAuthUrl,
    teamAuthorized,
    teamAuthUrl,
  },
  type,
}: SearchPlatformMenuItemProps) => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === UserType.Admin,
  );

  const isAuthorized =
    type !== Section.Available && (personalAuthorized || teamAuthorized);

  let mode: Mode;
  let modeLabel = null;

  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platform];
  if (!platformInfo) return null;
  const { icon, name } = platformInfo;

  const isPersonalSupported = installModes?.includes(
    ExternalSearchMode.Personal,
  );
  const isTeamSupported = installModes?.includes(ExternalSearchMode.TeamWide);

  if (isPersonalSupported && isTeamSupported) {
    mode = Mode.Both;
    modeLabel = teamAuthorized ? 'Individual' : 'Team or Individual';
  } else if (isPersonalSupported) {
    mode = Mode.PersonalOnly;
    modeLabel = 'Individual';
  } else if (isTeamSupported) {
    mode = Mode.TeamOnly;
    modeLabel = 'Team';
  }

  const logoNode = icon && (
    <div className={styles.logo}>
      <ExternalBrandLogo height={20} {...icon} />
      {isAuthorized && (
        <div className={styles.addedIconContainer}>
          <SVGIcon size={14} className={styles.addedIcon}>
            {addedIcon}
          </SVGIcon>
        </div>
      )}
    </div>
  );

  const clearButton = type === Section.Individual && (
    <Button
      className={styles.clearIcon}
      onClick={(e) => {
        e.stopPropagation(); // so user can clear multiple services without popover closing
        onRemove().then(() => {
          dispatch(
            UIActions.pushToast(
              miscToasts.platformRemovedFromSearchResults({
                platformName: name,
              }),
            ),
          );
        });
      }}
    >
      <SVGIcon size={10}>{clearIcon}</SVGIcon>
    </Button>
  );

  const addIntegration = () => {
    const open = (link?: string | null) => {
      if (link) {
        document.location.href = link;
      }

      return Promise.resolve();
    };

    if (mode === Mode.Both && !personalAuthorized && teamAuthorized) {
      dispatch(
        UIActions.openModal(ConfirmationModal, {
          title: 'Add personal integration?',
          description: `${name} search is already enabled teamwide. This will be superseded by your own account, which will usually return more results when you search.`,
          confirmButton: {
            text: 'Add personal',
            onClick: () => open(personalAuthUrl),
          },
        }),
      );
    } else if (!isAdmin) {
      open(personalAuthUrl);
    } else if (mode === Mode.PersonalOnly) {
      open(personalAuthUrl);
    } else if (mode === Mode.TeamOnly) {
      open(teamAuthUrl);
    } else if (mode === Mode.Both && personalAuthorized && !teamAuthorized) {
      open(teamAuthUrl);
    } else if (
      mode === Mode.Both &&
      !personalAuthorized &&
      !teamAuthorized &&
      teamAuthUrl
    ) {
      dispatch(
        UIActions.openModal(ConfirmationModal, {
          title: 'Add personal integration?',
          description: (
            <>
              You can also enable {name} search for your entire team in your{' '}
              <Link to={teamAuthUrl}>integration settings</Link>.
            </>
          ),
          confirmButton: {
            text: 'Add personal',
            onClick: () => open(personalAuthUrl),
          },
        }),
      );
    }
  };

  const children = (
    <div
      className={classNames(styles.container, {
        [styles.authorized]: isAuthorized,
      })}
    >
      <div className={styles.content}>
        {logoNode}
        <span className={styles.name}>{name}</span>
      </div>

      {clearButton}
    </div>
  );

  if (type === Section.Available)
    return (
      <MenuItem
        onClick={() => {
          addIntegration();
        }}
        wrap
        right={<span className={styles.platformModes}>{modeLabel}</span>}
      >
        {children}
      </MenuItem>
    );

  if (type === Section.Team && personalAuthorized)
    return (
      <MenuSimpleItem>
        <Tooltip
          content={`Your team account is\nsuperseded by your personal\n${name} account`}
          placement="bottom"
        >
          {children}
        </Tooltip>
      </MenuSimpleItem>
    );

  return <MenuSimpleItem>{children}</MenuSimpleItem>;
};

export default SearchPlatformMenuItem;
